import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	Input,
	OnInit,
	signal,
	ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, finalize, take } from 'rxjs';
import type { UserBasicInfo } from '@go-modules/interfaces/user';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ActivityEditAccessibilityAnnouncerService } from '../activity-edit-accessibility-announcer.service';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { HighlightPipe } from '@go-modules/pipes/highlight/highlight.pipe';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';

@Component({
	selector: 'activity-moderators',
	standalone: true,
	imports: [
		MatAutocompleteModule,
		MatChipsModule,
		MatFormFieldModule,
		MatInputModule,
		ReactiveFormsModule,
		TranslateModule,
		HighlightPipe
	],
	templateUrl: './activity-moderators.component.html',
	styleUrls: ['./activity-moderators.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityModeratorsComponent implements OnInit {
	@Input({required: true}) public activity: ActivityModel;
	@ViewChild('moderatorInput') private moderatorInput?: ElementRef<HTMLInputElement>;

	private readonly activityService = inject(NgxActivityService);
	private readonly accessibilityAnnouncer = inject(ActivityEditAccessibilityAnnouncerService);
	private readonly activityFormService = inject(ActivityFormService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly matDialog = inject(MatDialog);
	private readonly translate = inject(TranslateService);
	private readonly eligibleModerators = signal<UserBasicInfo[]>([]);
	private readonly selectedModeratorIds = signal<number[]>([]);
	private readonly searchTerm = signal('');

	public readonly searchControl = new FormControl('', {nonNullable: true});
	public readonly isLoading = signal(true);
	public readonly selectedModerators = computed(() => this.selectedModeratorIds().map((userId) =>
		this.eligibleModerators().find((user) => user.user_id === userId)
	));
	public readonly filteredModerators = computed(() => {
		const query = this.searchTerm().toLocaleLowerCase();
		const selectedIds = new Set(this.selectedModeratorIds());

		return this.eligibleModerators().filter((user) =>
			!selectedIds.has(user.user_id) &&
			(user.full_name.toLocaleLowerCase().includes(query) || user.email.toLocaleLowerCase().includes(query))
		);
	});

	public ngOnInit (): void {
		const moderatorControl = this.activityFormService.getControl('moderator_ids');
		this.selectedModeratorIds.set(moderatorControl?.value ?? []);
		moderatorControl?.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((moderatorIds: number[]) => this.selectedModeratorIds.set(moderatorIds));

		this.searchControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((query) => this.searchTerm.set(query));
		this.searchControl.valueChanges
			.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.announceSearchResults());

		this.loadModerators();
	}

	public loadModerators (): void {
		this.activityService.getEligibleModerators(this.activity.group_id)
			.pipe(
				take(1),
				finalize(() => this.isLoading.set(false))
			)
			.subscribe((moderators) => this.eligibleModerators.set(moderators));
	}

	public selectModerator (event: MatAutocompleteSelectedEvent): void {
		const moderator = event.option.value as UserBasicInfo;
		this.updateModeratorIds([...this.selectedModeratorIds(), moderator.user_id]);
		this.searchControl.setValue('');
		if (this.moderatorInput) {
			this.moderatorInput.nativeElement.value = '';
		}
		this.accessibilityAnnouncer.announce(
			this.translate.instant('activity-editor-moderators-added', {name: moderator.full_name})
		);
	}

	public requestRemoveModerator (moderator: UserBasicInfo): void {
		ConfirmDialogComponent.open(this.matDialog, {
			data: {
				title: this.translate.instant('activity-editor-moderators-remove-title'),
				message: this.translate.instant('activity-editor-moderators-remove-message', {
					name: moderator.full_name
				}),
				confirmText: this.translate.instant('common_remove')
			}
		}).afterClosed()
			.pipe(take(1), takeUntilDestroyed(this.destroyRef))
			.subscribe((confirmed) => {
				if (!confirmed) {
					return;
				}

				this.updateModeratorIds(
					this.selectedModeratorIds().filter((userId) => userId !== moderator.user_id)
				);
				this.accessibilityAnnouncer.announce(
					this.translate.instant('activity-editor-moderators-removed', {name: moderator.full_name})
				);
			});
	}

	public getRemoveLabel (moderator: UserBasicInfo): string {
		return this.translate.instant('activity-editor-moderators-remove-label', {name: moderator.full_name});
	}

	private updateModeratorIds (moderatorIds: number[]): void {
		const control = this.activityFormService.getControl('moderator_ids');
		control?.setValue(moderatorIds);
		control?.markAsDirty();
		this.activityFormService.markFormAsDirty();
	}

	private announceSearchResults (): void {
		const count = this.filteredModerators().length;
		this.accessibilityAnnouncer.announce(this.translate.instant(
			count === 0
				? 'activity-editor-moderators-no-results'
				: 'activity-editor-moderators-results',
			{count}
		));
	}
}
